import moment from 'moment-timezone';
import {List} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import * as ChildSelectors from 'web-app/react/entities/children/selectors';
import PlansEntity from 'web-app/react/entities/plans/index';
import ChildPlanBehaviorsEntity from 'web-app/react/entities/plans/child-plans-behaviors-entity';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {getActivePlan} from 'web-app/react/entities/plans/helpers';
import * as ManagePlanHelpers from 'web-app/react/entities/plans/helpers';
import ChildBehaviorIds from 'web-app/behaviors/child-behavior-ids';

import * as Constants from './constants';

export const showGenericFunding = createSelector(ChildSelectors.getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviorIds.ShowGenericFunding),
);

export const canManageGenericFunding = createSelector(ChildSelectors.getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviorIds.CanManageGenericFunding),
);

export const showDepositWidget = createSelector(ChildSelectors.getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviorIds.ShowDepositWidget),
);

export const plans = createSelector(PlansEntity.selectors.getByGroupedId, plans => plans || List());

export const child = ChildSelectors.getChildByProps;
export const monthsOld = createSelector(child, child =>
    child.birthday ? moment().diff(moment(child.birthday, 'YYYY-MM-DD'), 'months') : undefined,
);

export const childPlanBehaviors = createSelector(ChildPlanBehaviorsEntity.selectors.getById, childPlanBehaviors =>
    childPlanBehaviors ? childPlanBehaviors.behaviors : new BehaviorsCollection(),
);

export const currentPlan = createSelector(plans, getActivePlan);

export const futurePlans = createSelector(plans, plans => {
    const startOfDay = moment().startOf('day');
    return plans.filter(p => p.from && p.from.isAfter(startOfDay));
});

export const lastPlan = createSelector(plans, plans => plans.first());

export const currentPlanStatus = createSelector(currentPlan, futurePlans, child, (currentPlan, futurePlans, child) => {
    if (currentPlan) {
        return Constants.CURRENT_PLAN_STATUS_ACTIVE;
    } else if (futurePlans.size === 0) {
        return Constants.CURRENT_PLAN_STATUS_CAN_CREATE;
    } else if (futurePlans.size > 0) {
        // If the first future plan starts on the child startDate no current plan can be created
        if (!moment(futurePlans.last().from).isSame(moment(child.startDate), 'day')) {
            return Constants.CURRENT_PLAN_STATUS_CAN_CREATE;
        }
    }

    return null;
});

// A plan is considered a previous plan if the 'to date' is today or before today.
export const previousPlans = createSelector(plans, plans => {
    const startOfDay = moment().startOf('day');
    return plans.filter(p => p.to && p.to.isSameOrBefore(startOfDay));
});

export const firstDayANewFirstPlanCanBeValidOn = createSelector(child, child => {
    return moment(child.localStartDate).format(Constants.momentDayFormat);
});

const fromOfLatestScheduledPlan = createSelector(plans, child, (plans, child) => {
    if (plans.size === 0) {
        return moment(child?.localStartDate);
    } else {
        const latestPlan = plans.first();
        return latestPlan.from.clone();
    }
});

export const firstDayAfterLatestScheduledFrom = createSelector(fromOfLatestScheduledPlan, fromOfLatestScheduledPlan =>
    fromOfLatestScheduledPlan.add(1, 'day').format(Constants.momentDayFormat),
);

export const isLoadingPlans = createSelector(
    state => state.childProfile.childPlans.fetchingData,
    fetchingData => fetchingData,
);

const currentPlanStates = createSelector(currentPlan, plan => (plan ? plan.planStates : List()));

const currentPlanActivePlanState = createSelector(currentPlanStates, ManagePlanHelpers.getActivePlanState);

export const currentPlanPricingGroupId = createSelector(currentPlanActivePlanState, planState =>
    planState ? planState.pricingGroupId : null,
);
